import { billing, catalog, customers, identity, ordering, tallySync, unwrap, warehouse } from "../api";
import type { PagedResult } from "../../components/table/data-table";

/**
 * `GET /api/identity/audit` (`AuditController.cs`, US8) — the append-only audit trail (FR-074 –
 * FR-083). Read-only, no write route exists or ever will (FR-082). Gated by `identity.audit.read`;
 * a Company Admin is scoped to their own accounts server-side (FR-036) — there is no parameter here
 * that widens that, so nothing client-side needs to re-enforce it.
 *
 * Identity declares no response schema on any operation (GAP-4/GAP-5, same as `identity.ts`'s
 * `parseAccount`/`parsePermissions`), so the envelope is validated at runtime rather than assumed.
 */

export type AuditLogEntry = {
  id: string;
  occurredAt: string;
  eventType: string;
  actorAccountId: string | null;
  /** Identity's own name for the actor; null when there was none or it is erased. */
  actorName: string | null;
  /** `USR-000042` — the actor's URL key (`userPath`); null when Identity sent none. */
  actorNumber: string | null;
  subjectAccountId: string | null;
  subjectName: string | null;
  subjectNumber: string | null;
  /** Super Admin only (FR-074) — null for every other caller and for almost every entry. */
  submittedIdentifier: string | null;
  sourceIp: string | null;
  client: string | null;
  outcome: string;
  before: string | null;
  after: string | null;
};

const text = (value: unknown): string | null => (typeof value === "string" ? value : null);

function parseEntry(raw: Record<string, unknown>): AuditLogEntry {
  const id = raw.id === undefined || raw.id === null ? null : String(raw.id);
  const occurredAt = text(raw.occurredAt);
  const eventType = text(raw.eventType);
  const outcome = text(raw.outcome);

  if (id === null || occurredAt === null || eventType === null || outcome === null) {
    throw new Error(
      "GET /api/identity/audit answered with a row missing id, occurredAt, eventType or outcome " +
        "(AuditEntryView's own required fields). Nothing is rendered from a partial row (constitution VI).",
    );
  }

  return {
    id,
    occurredAt,
    eventType,
    actorAccountId: text(raw.actorAccountId),
    actorName: text(raw.actorName),
    actorNumber: text(raw.actorNumber),
    subjectAccountId: text(raw.subjectAccountId),
    subjectName: text(raw.subjectName),
    subjectNumber: text(raw.subjectNumber),
    submittedIdentifier: text(raw.submittedIdentifier),
    sourceIp: text(raw.sourceIp),
    client: text(raw.client),
    outcome,
    before: text(raw.before),
    after: text(raw.after),
  };
}

/**
 * Unlike the other paged list endpoints, `AuditPage` (`QueryAuditQuery.cs`) carries only `items`
 * and `totalCount` — no echoed `page`/`pageSize`. The envelope's page/size are the ones this call
 * sent, which is honest here because `readPaging` already keeps them inside the backend's own
 * `Math.Clamp(pageSize, 1, 200)` / `Math.Max(1, page)` bounds (`paging.ts`'s `PAGE_SIZES` tops out
 * at 25), so nothing this screen requests ever lands outside what the server actually used.
 */
function parsePage(body: unknown, page: number, pageSize: number): PagedResult<AuditLogEntry> {
  const raw = (body ?? {}) as Record<string, unknown>;
  const items = raw.items;
  const totalCount = typeof raw.totalCount === "number" ? raw.totalCount : Number(raw.totalCount);

  if (!Array.isArray(items) || !Number.isFinite(totalCount)) {
    throw new Error(
      "GET /api/identity/audit answered without a paged envelope (items, totalCount). Nothing is " +
        "inferred from a partial one.",
    );
  }

  return {
    items: items.map((item) => parseEntry((item ?? {}) as Record<string, unknown>)),
    page,
    pageSize,
    totalCount,
  };
}

export type AuditLogFilters = {
  subjectAccountId?: string;
  eventType?: string;
  from?: string;
  to?: string;
};

export async function listAuditLog(
  params: AuditLogFilters & { page: number; pageSize: number },
): Promise<PagedResult<AuditLogEntry>> {
  const { page, pageSize } = params;
  const body = await unwrap(
    (await identity()).GET("/api/identity/audit", {
      params: {
        query: {
          subjectAccountId: params.subjectAccountId,
          eventType: params.eventType,
          from: params.from,
          to: params.to,
          page,
          pageSize,
        },
      },
    }),
  );
  return parsePage(body, page, pageSize);
}

/**
 * `AuditEventType.cs`'s 30 members, spelled the way `AuditEventTypeExtensions.WireName` puts them
 * on the wire (`sign_in.success`, `session.reuse_detected`, …) — a fixed system vocabulary, not a
 * role or a permission, so listing it here is the same kind of display-label map as
 * `customers.ts`'s `CUSTOMER_STATUS_LABEL` rather than the hard-coded-role pattern rule 3 forbids.
 */
export const AUDIT_EVENT_TYPES = [
  "sign_in.success",
  "sign_in.failure",
  "otp.requested",
  "otp.verified",
  "otp.failed",
  "session.issued",
  "session.refreshed",
  "session.signed_out",
  "session.revoked",
  "session.reuse_detected",
  "source.throttled",
  "account.locked",
  "account.created",
  "account.invited",
  "invitation.resent",
  "invitation.revoked",
  "invitation.accepted",
  "account.activated",
  "account.deactivated",
  "account.reactivated",
  "account.unlocked",
  "account.erased",
  "password.set",
  "password.changed",
  "password.reset",
  "role.changed",
  "matrix.changed",
  "authorization.refused",
  "bulk.operation",
] as const;

export function eventTypeLabel(wireName: string): string {
  const words = wireName.replace(/[._]/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * §19's other audit logs — price and tier changes, credit and tier assignment, order approvals,
 * packing entries and overrides, invoices and payments, Tally sync — each kept by the service that
 * owns the record and read through that service's own `GET …/admin/audit` (all gated by
 * `identity.audit.read`, all one row shape). Identity's trail above stays the default view.
 */
export const AUDIT_SOURCES = [
  { key: "catalog", label: "Catalogue & pricing" },
  { key: "customers", label: "Customers & credit" },
  { key: "orders", label: "Orders & approvals" },
  { key: "warehouse", label: "Packing & inventory" },
  { key: "billing", label: "Invoicing & payments" },
  { key: "tallysync", label: "Tally sync" },
] as const;

export type AuditSource = (typeof AUDIT_SOURCES)[number]["key"];

export const isAuditSource = (value: string | null | undefined): value is AuditSource =>
  AUDIT_SOURCES.some((source) => source.key === value);

export type ServiceAuditEntry = {
  id: string;
  occurredAt: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  /** The record's own name/number, sent by the service (entityName, entityLabel or orderNumber by service). */
  entityLabel: string | null;
  actorId: string | null;
  actorName: string | null;
  summary: string | null;
};

async function readServiceAudit(source: AuditSource, query: { page: number; pageSize: number }) {
  const params = { params: { query } };
  switch (source) {
    case "catalog":
      return unwrap((await catalog()).GET("/api/catalog/admin/audit", params));
    case "customers":
      return unwrap((await customers()).GET("/api/customers/admin/audit", params));
    case "orders":
      return unwrap((await ordering()).GET("/api/orders/admin/audit", params));
    case "warehouse":
      return unwrap((await warehouse()).GET("/api/warehouse/admin/audit", params));
    case "billing":
      return unwrap((await billing()).GET("/api/billing/admin/audit", params));
    case "tallysync":
      return unwrap((await tallySync()).GET("/api/tallysync/admin/audit", params));
  }
}

export async function listServiceAudit(
  source: AuditSource,
  page: number,
  pageSize: number,
): Promise<PagedResult<ServiceAuditEntry>> {
  const raw = ((await readServiceAudit(source, { page, pageSize })) ?? {}) as Record<string, unknown>;
  const totalCount = Number(raw.totalCount);
  if (!Array.isArray(raw.items) || !Number.isFinite(totalCount)) {
    throw new Error(`The ${source} audit log answered without a paged envelope (items, totalCount).`);
  }
  return {
    page,
    pageSize,
    totalCount,
    items: raw.items.map((item: Record<string, unknown>) => {
      if (item.id == null || typeof item.occurredAt !== "string" || typeof item.action !== "string") {
        throw new Error(`The ${source} audit log sent a row missing id, occurredAt or action.`);
      }
      return {
        id: String(item.id),
        occurredAt: item.occurredAt,
        action: item.action,
        entityType: text(item.entityType),
        entityId: item.entityId == null ? null : String(item.entityId),
        entityLabel: text(item.entityName) ?? text(item.entityLabel) ?? text(item.orderNumber),
        actorId: text(item.actorId),
        actorName: text(item.actorName),
        summary: text(item.summary),
      };
    }),
  };
}
